"use client";
import {
  CreatableSelect,
  UploadDocumentCard,
  VeCountry,
  VeInput,
  VeRadioGroup,
  VeSelect,
  VeTags,
} from "@packages/ui/components";
import { SubmitHandler, useForm, z, zodResolver } from "@packages/ui/libs";
import { CircularProgress, Grid, VeBox, VeTypography } from "@packages/ui/mui";
import { useEffect, useRef, useState } from "react";
import { RemoveIcon } from "./RemoveIcon";
import _ from "lodash";
// import DocumentCard from "./DocumentCard";

const validationSchema = z
  .object({
    projectName: z
      .string({
        required_error: "Enter project name",
      })
      .min(1, "Enter project name"),
    projectStage: z
      .string({
        required_error: "Select Project Stage",
      })
      .min(1, "Select Project Stage"),
    country: z
      .string({
        required_error: "Select Country",
      })
      .min(1, "Select Country"),
    sectorGroup: z
      .string({
        required_error: "Select Sector Group",
      })
      .min(1, "Select Sector Group"),
    sector: z
      .string({
        required_error: "Select Sector",
      })
      .min(1, "Select Sector"),
    subSectorGroup: z
      .string({
        required_error: "Select Sub-Sector Group",
      })
      .min(1, "Select Sub-Sector Group"),
    registry: z.optional(z.string().nullable()),
    methodology: z.optional(z.string().nullable()),
    financingType: z
      .array(
        z.string({
          required_error: "Select Financing type",
        })
      )
      .min(1, "Select Financing type"),
    isMethodologyDeviation: z.optional(z.string().nullable()),
    projectLength: z.optional(z.string().nullable()),
    coCertifications: z.optional(z.string().nullable()),
    deviationDetails: z.optional(z.string().nullable()),
    isMainProponent: z
      .string({
        required_error: "This is a required field",
      })
      .min(1, "This is a required field"),

    // Additional fields based on `isMainProponent`
    companyName: z
      .string({
        required_error: "Enter Company Name",
      })
      .optional(),
    address1: z
      .string({
        required_error: "Enter Address Line 1",
      })
      .optional(),
    address2: z.string().optional(),
    city: z
      .string({
        required_error: "Enter City",
      })
      .optional(),
    state: z.string().optional(),
    registrationCountry: z
      .string({
        required_error: "Select registration Country",
      })
      .optional(),
    zipcode: z
      .string({
        required_error: "Enter Zip Code",
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.isMainProponent === "no") {
      if (!data.companyName || data.companyName.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["companyName"],
          message: "Company Name is required.",
        });
      }
      if (!data.address1 || data.address1.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["address1"],
          message: "Address Line 1 is required.",
        });
      }
      if (!data.city || data.city.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["city"],
          message: "City is required.",
        });
      }
      if (!data.registrationCountry || data.registrationCountry.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["registrationCountry"],
          message: "Registration Country is required.",
        });
      }
      if (!data.zipcode || data.zipcode.trim() === "") {
        ctx.addIssue({
          code: "custom",
          path: ["zipcode"],
          message: "Zip Code is required.",
        });
      }
    }
  });

type ValidationSchema = z.infer<typeof validationSchema>;

// function formatFileSize(sizeInBytes: number) {
//     if (sizeInBytes < 1024) {
//         return `${sizeInBytes} Bytes`;
//     } else if (sizeInBytes < 1048576) {
//         return `${(sizeInBytes / 1024).toFixed(2)} KB`;
//     } else if (sizeInBytes < 1073741824) {
//         return `${(sizeInBytes / 1048576).toFixed(2)} MB`;
//     } else {
//         return `${(sizeInBytes / 1073741824).toFixed(2)} GB`;
//     }
// }

const ProjectStep1 = ({
  variables,
  assets,
  DynamicImage,
  editToggle,
  refetch,
  toast,
  useMutation,
  useQueryClient,
  useSession,
  getDirtyValues,
  mutateAction,
  submitCurrentStep,
  setSubmitCurrentStep,
  setSelectedTab,
  setIsLoading,
  setProjectDetails,
  projectDetails: data,
  RichText,
}: any) => {
  const queryClient = useQueryClient();
  const { data: session, update } = useSession();
  const formRef = useRef<any>(null);
  const isMobile = variables.useMediaQuery("(max-width: 600px)");
  const [coordinates, setCoordinates] = useState<any>([
    {
      latitude: "",
      longitude: "",
    },
  ]);
  const [files, setFiles] = useState<any>([]);
  const [filesCopy, setFileCopy] = useState<any>([]);
  const [coordinatesCopy, setCoordinatesCopy] = useState<any>([
    {
      latitude: "",
      longitude: "",
    },
  ]);
  const [sectorObject, setSectorObject] = useState([]);
  const [sectorGroupList, setSectorGroupList] = useState<any>([]);
  const [sectorList, setSectorList] = useState<any>([]);
  const [finalSectorDropdownList, setFinalSectorDropdown] = useState([]);
  const [finalSubSectorDropdownList, setFinalSubSectorDropdown] = useState<any>(
    []
  );
  const [fileUploading, setFileUploading] = useState(false);
  const [registryList, setRegistryList] = useState([]);
  const [methodologyList, setMethodologyList] = useState<any>([]);
  const [financingTypeList, setFinancingTypeList] = useState([]);
  const [projectStageList, setProjectStageList] = useState([]);
  const bottomRef = useRef<HTMLElement | null>(null);
  const [fileKey, setFileKey] = useState({
    key: "",
    actionType: "",
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    control,
    watch,
    clearErrors,
    formState: { dirtyFields, errors, isSubmitted },
  }: any = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      projectName: "",
      projectStage: "",
      country: "",
      sectorGroup: "",
      sector: "",
      subSectorGroup: "",
      registry: "",
      methodology: "",
      financingType: [],
      isMethodologyDeviation: "",
      projectLength: "",
      coCertifications: "",
      deviationDetails: "",
      isMainProponent: "",
      companyName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      registrationCountry: "",
      zipcode: "",
    },
  });

  useEffect(() => {
    // This effect will trigger on every form state change
    if (Object.keys(errors)?.length === 1) {
      if (errors?.isMainProponent?.message?.length > 0) {
        if (bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }
      // Your custom logic when the form has been submitted and validated
    }
  }, [isSubmitted, errors]);

  const resetForm = () => {
    // alert("hello")
    const {
      projectName,
      projectStatus,
      country,
      sectorGroup,
      sector,
      subSectorGroup,
      registry,
      methodology,
      financingType,
      isMethodologyDeviation,
      projectLength,
      coCertifications,
      deviationDetails,
      isMainProponent,
      mainProponentDetails,
    } = data;

    const {
      companyName,
      address1,
      address2,
      city,
      state,
      registrationCountry,
      zipcode,
    } = mainProponentDetails || {};

    reset({
      projectName,
      projectStage: projectStatus,
      country,
      sectorGroup,
      sector,
      subSectorGroup,
      registry,
      methodology,
      financingType: financingType || [],
      isMethodologyDeviation:
        isMethodologyDeviation == null
          ? ""
          : isMethodologyDeviation === false
            ? "no"
            : "yes",
      projectLength: projectLength === "0" ? "" : projectLength || "",
      coCertifications,
      deviationDetails: deviationDetails || "",
      isMainProponent: isMainProponent === false ? "no" : "yes",
      companyName: isMainProponent === false ? companyName || "" : "",
      address1: isMainProponent === false ? address1 || "" : "",
      address2: isMainProponent === false ? address2 || "" : "",
      city: isMainProponent === false ? city || "" : "",
      state: isMainProponent === false ? state || "" : "",
      registrationCountry:
        isMainProponent === false ? registrationCountry || "" : "",
      zipcode: isMainProponent === false ? zipcode || "" : "",
    });
  };

  const isMainProponent = watch("isMainProponent");

  const values: any = watch();

  useEffect(() => {
    if (isMainProponent === "yes") {
      clearErrors([
        "companyName",
        "address1",
        "city",
        "registrationCountry",
        "zipcode",
      ]);
    }
  }, [isMainProponent, clearErrors]);

  useEffect(() => {
    if (data) {
      resetForm();
      if (data?.area?.length > 0) {
        setCoordinates(data?.area);
        setCoordinatesCopy(data?.area);
      }

      if (data?.boundryFile) {
        setFiles([
          {
            s3ObjectKey: data?.boundryFile,
            fileSize: 0,
            status: "ACTIVE",
            originalFileName: data?.boundryFile,
            objectKey: data?.boundryFile,
          },
        ]);
        setFileCopy([
          {
            s3ObjectKey: data?.boundryFile,
            fileSize: 0,
            status: "ACTIVE",
            originalFileName: data?.boundryFile,
            objectKey: data?.boundryFile,
          },
        ]);
      }
    }
  }, [data]);

  // console.log("proj", values, data, sectorObject, finalSectorDropdownList, finalSubSectorDropdownList, sectorGroupList);

  const { data: projectTypesData } = variables.useQueryData({
    queryKey: ["project-type-list"],
    endPoint: `/project/v2/onboarding/dropdown/project-type`,
    enabled: assets.isOpen,
  });

  const { data: methodologyData } = variables.useQueryData({
    queryKey: ["methodology-list"],
    endPoint: `/project/v2/onboarding/dropdown/methodologies`,
    enabled: assets.isOpen,
  });

  const { data: registryData } = variables.useQueryData({
    queryKey: ["registry-list"],
    endPoint: `/project/v2/onboarding/dropdown/registries`,
    enabled: assets.isOpen,
  });

  const { data: financingTypeData } = variables.useQueryData({
    queryKey: ["financiang-type-list"],
    endPoint: `/project/v2/onboarding/dropdown/financing-types`,
    enabled: assets.isOpen,
  });

  const { data: projectStageData } = variables.useQueryData({
    queryKey: ["project-stage-list"],
    endPoint: `/project/v2/onboarding/dropdown/project-stages`,
    enabled: assets.isOpen,
  });

  useEffect(() => {
    if (registryData?.registries) {
      const mappedRoles = registryData?.registries?.map((data: any) => ({
        label: data?.registryName,
        value: `${data?.id}.${data?.registryName}`,
      }));
      setRegistryList(mappedRoles);
    } else {
      setRegistryList([]);
    }
  }, [registryData]);

  useEffect(() => {
    if (values?.registry || data?.registry) {
      const value = values?.registry || data?.registry;
      const mappedRoles = registryData?.registries
        ?.filter((data: any) => `${data?.id}.${data?.registryName}` === value)
        ?.flatMap(
          (data: any) =>
            data?.methodologies?.map((innerItem: any) => ({
              label: innerItem?.methodologyName,
              value: `${innerItem?.id}.${innerItem?.methodologyName}`,
            })) || []
        );

      if (methodologyData?.methodologies) {
        const defaultList = methodologyData?.methodologies?.map(
          (innerItem: any) => ({
            label: innerItem?.methodologyName,
            value: `${innerItem?.id}.${innerItem?.methodologyName}`,
          })
        );
        setMethodologyList([...mappedRoles, ...defaultList]);
      } else setMethodologyList(mappedRoles);
    }
    // else setMethodologyList([]);
  }, [values?.registry, registryList, data?.registry]);

  useEffect(() => {
    if (methodologyData?.methodologies) {
      const mappedRoles = methodologyData?.methodologies?.map(
        (innerItem: any) => ({
          label: innerItem?.methodologyName,
          value: `${innerItem?.id}.${innerItem?.methodologyName}`,
        })
      );
      setMethodologyList([...methodologyList, ...mappedRoles]);
    }
  }, [methodologyData]);

  useEffect(() => {
    if (projectTypesData?.sectorGroup) {
      const entireObject = projectTypesData?.sectorGroup?.map(
        (data: any) => data
      );
      setSectorObject(entireObject);
    } else {
      setSectorObject([]);
    }
  }, [projectTypesData]);

  useEffect(() => {
    if (sectorObject) {
      const mappedGroup = sectorObject?.map((data: any) => ({
        label: data?.name?.replaceAll("_", " "),
        value: `${data?.id}.${data?.name}`,
      }));
      setSectorGroupList(mappedGroup);
    } else setSectorGroupList([]);
  }, [sectorObject, data?.sector]);

  useEffect(() => {
    if (values?.sectorGroup || data?.sectorGroup) {
      const value = values?.sectorGroup || data?.sectorGroup;
      const mappedsector = sectorObject
        ?.filter((data: any) => `${data?.id}.${data?.name}` === value)
        ?.flatMap((innerData: any) => innerData?.sectorLists);
      setSectorList(mappedsector);

      const mapped: any = mappedsector?.map((data: any) => ({
        label: data?.name?.replaceAll("_", " "),
        value: `${data?.id}.${data?.name}`,
      }));
      setFinalSectorDropdown(mapped);
    } else setSectorList([]);
  }, [values?.sectorGroup, data?.sectorGroup, sectorGroupList]);

  useEffect(() => {
    if (values?.sector || data?.sector) {
      const value = values?.sector || data?.sector;
      const mappedsector = sectorList
        ?.filter((data: any) => `${data?.id}.${data?.name}` === value)
        ?.flatMap((innerData: any) => innerData?.subsectorGroups);
      const mapped = mappedsector?.map((data: any) => ({
        label: data?.name?.replaceAll("_", " "),
        value: `${data?.id}.${data?.name}`,
      }));
      setFinalSubSectorDropdown(mapped);
    }
  }, [values?.sector, data?.sector, finalSectorDropdownList]);

  useEffect(() => {
    if (financingTypeData?.financingTypes) {
      const mappedRoles = financingTypeData?.financingTypes?.map(
        (data: any) => ({
          label: data?.name?.replaceAll("_", " "),
          value: `${data?.id}.${data?.name}`,
        })
      );
      setFinancingTypeList(mappedRoles);
    } else {
      setFinancingTypeList([]);
    }
  }, [financingTypeData]);

  useEffect(() => {
    if (projectStageData?.projectStages) {
      const mappedRoles = projectStageData?.projectStages?.map((data: any) => ({
        label: data?.stageName?.replaceAll("_", " "),
        value: `${data?.id}.${data?.stageName}`,
      }));
      setProjectStageList(mappedRoles);
    } else {
      setProjectStageList([]);
    }
  }, [projectStageData]);

  const fields = [
    {
      name: "projectName",
      label: "Enter Your Project Name",
      value: data?.projectName || "",
      required: true,
      section: "basic",
    },
    {
      name: "projectStage",
      label: "Project Stage",
      value: data?.projectStatus || "",
      required: true,
      options: projectStageList,
      section: "basic",
    },
    {
      name: "country",
      label: "Country",
      value: data?.country || "",
      required: true,
      section: "basic",
    },
    {
      name: "sectorGroup",
      label: "Sector Group",
      value: data?.sectorGroup || "",
      required: true,
      options: sectorGroupList,
      section: "basic",
    },
    {
      name: "sector",
      label: "Sector",
      value: data?.sector || "",
      required: true,
      options: finalSectorDropdownList,
      section: "basic",
    },
    {
      name: "subSectorGroup",
      label: "Sub Sector Group",
      value: data?.subSectorGroup || "",
      required: true,
      options: finalSubSectorDropdownList,
      section: "basic",
    },
    {
      name: "registry",
      label: "Registry",
      value: data?.registry || "",
      required: false,
      section: "basic",
      options: registryList,
    },
    {
      name: "methodology",
      label: "Methodology",
      value: data?.methodology || "",
      required: false,
      options: methodologyList,
      section: "basic",
    },
    {
      name: "isMethodologyDeviation",
      label: "Are you deviating from the methodology?",
      value:
        data?.isMethodologyDeviation == null
          ? ""
          : data?.isMethodologyDeviation === false
            ? "no"
            : "yes",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      section: "basic",
    },
    {
      name: "deviationDetails",
      label: "Deviation Details",
      value: data?.deviationDetails || "",
      section: "basic",
    },
    {
      name: "financingType",
      label: "Financing Type",
      value: data?.financingType || [],
      required: true,
      options: financingTypeList,
      section: "basic",
    },
    {
      name: "projectLength",
      label: "Project Length",
      value: data?.projectLength === "0" ? "" : data?.projectLength || "",
      section: "location",
    },
    {
      name: "coCertifications",
      label: "Co-Certifications",
      value: data?.coCertifications || "",
      section: "location",
      options: variables.configData?.configurations
        ?.find((item: any) => item?.key === "COCERTIFICATION_DROPDOWN")
        ?.value?.split(",")
        .map((item: any) => {
          return {
            value: item,
            label: item,
          };
        }),
    },
    {
      name: "isMainProponent",
      label: `Is ${variables?.userData?.user?.organisationName} the main project proponent of the company?`,
      required: true,
      value: data?.isMainProponent
        ? data?.isMainProponent === false
          ? "no"
          : "yes"
        : "",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      section: "proponent",
    },
    {
      name: "companyName",
      label: "Company Name",
      value: data?.isMainProponent === false ? data?.companyName || "" : "",
      required: true,
      section: "proponent",
    },
    {
      name: "address1",
      label: "Address Line 1",
      value: data?.isMainProponent === false ? data?.address1 || "" : "",
      required: true,
      section: "proponent",
    },
    {
      name: "address2",
      label: "Address Line 2",
      value: data?.isMainProponent === false ? data?.address2 || "" : "",
      section: "proponent",
    },
    {
      name: "city",
      label: "City",
      value: data?.isMainProponent === false ? data?.city || "" : "",
      required: true,
      section: "proponent",
    },
    {
      name: "state",
      label: "State",
      value: data?.isMainProponent === false ? data?.state || "" : "",
      section: "proponent",
    },
    {
      name: "registrationCountry",
      label: "Registration Country",
      value:
        data?.isMainProponent === false ? data?.registrationCountry || "" : "",
      required: true,
      section: "proponent",
    },
    {
      name: "zipcode",
      label: "Zip Code",
      value: data?.isMainProponent === false ? data?.zipcode || "" : "",
      required: true,
      section: "proponent",
    },
  ];

  useEffect(() => {
    if (!assets.isOpen && !data) {
      reset({
        projectName: "",
        projectStage: "",
        country: "",
        sectorGroup: "",
        sector: "",
        subSectorGroup: "",
        registry: "",
        methodology: "",
        financingType: [],
        isMethodologyDeviation: "",
        projectLength: "",
        coCertifications: "",
        deviationDetails: "",
        isMainProponent: "",
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        registrationCountry: "",
        zipcode: "",
      });
    }
  }, [assets.isOpen]);

  const { mutate, isPending } = useMutation({
    mutationFn: mutateAction,
    mutationKey: ["create-project"],
    onSuccess: async (response: any) => {
      if (response.error)
        throw new Error(response.error?.heading, {
          cause: response?.error?.message,
        });
      queryClient.setQueryData(["project-update"], response);
      // toast(`Project ${!data ? "created" : "updated"} successfully!`, { type: "success" });
      setSelectedTab(1);
      setIsLoading(false);
      !data
        ? setProjectDetails(response)
        : setProjectDetails(response?.updated_project_data);
      data && variables?.projectRefetch();
    },
    onError: (error: any) => {
      setIsLoading(false);
      toast(error?.cause as string, { type: "error" });
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const maxSize = 250;
    const supportedFileTypes = [
      ".kml",
      ".kmz",
      ".shp",
      ".shx",
      ".dbf",
      ".geojson",
      ".gpkg",
      ".gpx",
      ".tiff",
      ".tif",
      ".csv",
      ".gml",
      ".dxf",
    ];
    const files = e?.target?.files;
    if (!files || files.length === 0) {
      return;
    }
    const newSelectedFiles = Array.from(files);
    if (maxSize) {
      const validationErrors = newSelectedFiles
        .map((file) => {
          const fileSize = file.size;
          const fileName = file.name.replaceAll(" ", "")?.toLowerCase();
          const isSupportedFileType = supportedFileTypes.some((type) =>
            fileName.endsWith(type)
          );
          if (!isSupportedFileType)
            return "Unsupported File Format. Please try again!";
          if (fileSize >= maxSize * 1024 * 1024) {
            return `Maximum file size could not be greater than ${maxSize} MB`;
          }
          return null; // No error for this file
        })
        .filter((msg) => msg !== null);
      if (validationErrors.length > 0) {
        toast(validationErrors.join(". "), { type: "error" }); // Combine all errors into a single message
        return;
      }
    }

    if (files && files.length > 0) {
      setFileUploading(true);
    }
    try {
      const formData = new FormData();
      const fileInfos: any = [];
      Array.from(files).forEach((file: File) => {
        const pattern = /[^a-zA-Z0-9 _.-]/g;
        const fileName = file?.name.replace(pattern, "_");
        const newFile = new File([file], fileName, { type: file.type });
        formData.append("files", newFile);
        if (fileName && file?.size > 0) {
          fileInfos.push({
            s3ObjectKey: file?.name,
            fileSize: file?.size,
            status: "ACTIVE",
            originalFileName: file?.name,
            comment: "",
            commentStatus: "",
            objectKey: "",
          });
        }
      });
      formData.append("bucket", variables?.bucketType);
      const data = await variables?.uploadFiles({
        data: formData,
        type: "post",
        endPoint: "/files/upload",
        session,
        headersReqWithSession: {
          "Content-Type": `multipart/form-data`,
        },
        update,
      });

      const updatedFileInfos = fileInfos?.map((item: any, index: number) => ({
        ...item,
        objectKey: data?.fileNames[index]?.objectKey,
        s3ObjectKey: data?.fileNames[index]?.objectKey,
      }));
      if (!data?.error) {
        setFiles(updatedFileInfos);
        setFileUploading(false);
      } else {
        toast("Something went wrong!.", { type: "error" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (submitCurrentStep?.step1) {
      if (formRef.current) {
        formRef.current.dispatchEvent(
          new Event("submit", { bubbles: true, cancelable: true })
        );
      }
      setSubmitCurrentStep({
        ...submitCurrentStep,
        step1: false,
      });
    }
  }, [submitCurrentStep]);

  // console.log("values", values, finalSubSectorDropdownList, finalSectorDropdownList, sectorGroupList);

  const onSubmit: SubmitHandler<ValidationSchema> = (dataFields: any) => {
    const finalData = getDirtyValues(dataFields, dirtyFields);
    const {
      companyName,
      address1,
      address2,
      city,
      state,
      zipcode,
      registrationCountry,
      ...filteredFinalData
    } = finalData;

    const dynamicProponentDetails: any = {};
    const proponentFields = [
      "companyName",
      "address1",
      "address2",
      "city",
      "state",
      "zipcode",
      "registrationCountry",
    ];

    for (let i = 0; i < proponentFields.length; i++) {
      const field: any = proponentFields[i];
      console.log("field", field, dirtyFields?.[`${field}`]);
      if (dirtyFields?.[`${field}`]) {
        console.log("in", dynamicProponentDetails[field], finalData[field]);
        dynamicProponentDetails["companyName"] = dataFields?.companyName;
        dynamicProponentDetails["address1"] = dataFields?.address1;
        dynamicProponentDetails["address2"] = dataFields?.address2;
        dynamicProponentDetails["city"] = dataFields?.city;
        dynamicProponentDetails["state"] = dataFields?.state;
        dynamicProponentDetails["zipcode"] = dataFields?.zipcode;
        dynamicProponentDetails["registrationCountry"] =
          dataFields?.registrationCountry;
        break; // Exit the loop
      }
    }

    const coordinatesEqual = _.isEqual(coordinates, coordinatesCopy);
    const filesEqual = _.isEqual(files, filesCopy);

    // console.log("dddd", coordinates, coordinatesCopy, files, filesCopy, coordinatesEqual, filesEqual, filteredFinalData);

    if (
      _.isEmpty(filteredFinalData) &&
      coordinatesEqual &&
      filesEqual &&
      _.isEmpty(dynamicProponentDetails)
    ) {
      setSelectedTab(1);
      return;
    }

    if (
      coordinates.filter(
        (item: any) =>
          (item?.latitude?.length === 0 && item?.longitude?.length > 0) ||
          (item?.latitude?.length > 0 &&
            (Number(item?.latitude) < -90 || Number(item?.latitude) > 90)) ||
          (item?.latitude?.length > 0 && item?.longitude?.length === 0) ||
          (item?.longitude?.length > 0 &&
            (Number(item?.longitude) < -180 || Number(item?.longitude) > 180))
      )?.length > 0
    )
      return;

    setIsLoading(true);
    mutate({
      type: data ? "patch" : "post",
      endPoint: data
        ? `/project/v2/onboarding/${data?.id}`
        : "/project/v2/onboarding",
      data: {
        ...filteredFinalData,
        ...(!data
          ? {
              subSectorGroupId: dataFields?.subSectorGroup?.split(".")?.[0],
            }
          : {}),
        ...(!data
          ? { projectStageId: dataFields?.projectStage?.split(".")?.[0] }
          : {}),
        ...(coordinates?.filter(
          (item: any) =>
            item?.latitude?.length > 0 && item?.longitude?.length > 0
        )?.length > 0
          ? {
              area: !data
                ? coordinates
                    .filter(
                      (item: any) =>
                        item?.latitude?.length > 0 &&
                        item?.longitude?.length > 0
                    )
                    ?.map((item: any) => {
                      return {
                        longitude: Number(item?.longitude)?.toFixed(6),
                        latitude: Number(item?.latitude)?.toFixed(6),
                      };
                    })
                : coordinates
                    .filter(
                      (item: any) =>
                        item?.latitude?.length > 0 &&
                        item?.longitude?.length > 0
                    )
                    ?.map((item: any) => {
                      return {
                        longitude: Number(item?.longitude)?.toFixed(6),
                        latitude: Number(item?.latitude)?.toFixed(6),
                      };
                    }),
            }
          : {}),
        ...(!data ? { projectOnboardingStatus: "draft" } : {}),
        // ...(files?.length > 0 ? { boundryFile: files[0]?.objectKey } : {}),
        boundryFile: files?.length > 0 ? files[0]?.objectKey : null,
        ...(dataFields?.isMethodologyDeviation?.length > 0
          ? {
              isMethodologyDeviation:
                dataFields?.isMethodologyDeviation === "yes",
            }
          : {}),
        ...(dataFields?.isMainProponent === "no" &&
        Object.keys(dynamicProponentDetails).length > 0
          ? { mainProponentDetails: dynamicProponentDetails }
          : {}),
        isMainProponent: dataFields?.isMainProponent === "yes",
        ...(!data ? { orgId: variables?.userData?.user?.organisationId } : {}),
        stepper: data
          ? {
              ...data?.stepper,
              basicProjectDetails: "COMPLETED",
            }
          : {
              basicProjectDetails: "COMPLETED",
              documents: "NOT_STARTED",
              applicationForm: "NOT_STARTED",
              volumePricing: "NOT_STARTED",
            },
      },
      session,
      update,
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <VeBox>
          <VeBox display={"flex"} flexDirection={"column"}>
            <VeTypography variant="subtitle1Main">
              Basic Project Details
            </VeTypography>
            <VeTypography
              variant="body2Tertiary"
              marginTop={"8px"}
              marginBottom={"32px"}
            >
              Note: Please be advised that once you select{" "}
              <VeTypography
                variant="body2StrongerSecondary"
                marginTop={"8px"}
                marginBottom={"32px"}
                sx={{ color: "#646E83 !important" }}
              >
                Save
              </VeTypography>
              , the{" "}
              <VeTypography
                variant="body2StrongerSecondary"
                marginTop={"8px"}
                marginBottom={"32px"}
                sx={{ color: "#646E83 !important" }}
              >
                Project Stage
              </VeTypography>{" "}
              and{" "}
              <VeTypography
                variant="body2StrongerSecondary"
                marginTop={"8px"}
                marginBottom={"32px"}
                sx={{ color: "#646E83 !important" }}
              >
                Sub-Sector Group
              </VeTypography>{" "}
              fields will be finalized and no longer editable.
            </VeTypography>
          </VeBox>
          <Grid container columnSpacing={"16px"}>
            {fields
              ?.filter((item: any) => item.section === "basic")
              ?.map((item: any) => (
                <Grid
                  sx={{
                    display:
                      values["isMethodologyDeviation"] !== "yes" &&
                      item?.label === "Deviation Details"
                        ? "none"
                        : "",
                  }}
                  item
                  xl={
                    item?.label === "Financing Type" ||
                    item?.label === "Deviation Details" ||
                    item?.name === "isMethodologyDeviation" ||
                    item?.name === "projectName" ||
                    item?.name === "methodology"
                      ? 12
                      : 6
                  }
                  lg={
                    item?.label === "Financing Type" ||
                    item?.label === "Deviation Details" ||
                    item?.name === "isMethodologyDeviation" ||
                    item?.name === "projectName" ||
                    item?.name === "methodology"
                      ? 12
                      : 6
                  }
                  md={
                    item?.label === "Financing Type" ||
                    item?.label === "Deviation Details" ||
                    item?.name === "isMethodologyDeviation" ||
                    item?.name === "projectName" ||
                    item?.name === "methodology"
                      ? 12
                      : 6
                  }
                  sm={
                    item?.label === "Financing Type" ||
                    item?.name === "isMethodologyDeviation"
                      ? 12
                      : 6
                  }
                  xs={12}
                  mb={2}
                >
                  <VeBox>
                    {item?.label === "Country" ? (
                      <VeCountry
                        value={item?.value}
                        register={register}
                        setValue={setValue}
                        name={item?.name}
                        label="Country"
                        required
                        error={!!errors?.[`${item?.name!}`]}
                        errorText={errors?.[item?.name!]?.message}
                      />
                    ) : item?.name === "isMethodologyDeviation" ||
                      item?.name === "isMainProponent" ? (
                      <VeBox>
                        <VeTypography
                          variant="label2StrongTertiary"
                          marginTop={"20px !important"}
                          sx={{ fontWeight: "500 !important" }}
                        >
                          {item?.label}
                        </VeTypography>
                        <VeBox sx={{ marginTop: "5px", marginLeft: "5px" }}>
                          <VeRadioGroup
                            name={item?.name}
                            options={item?.options}
                            defaultValue={values[item?.value]}
                            control={control}
                          />
                        </VeBox>
                      </VeBox>
                    ) : item?.label === "Sector Group" ||
                      item?.label === "Sub Sector Group" ||
                      item?.label === "Sector" ||
                      item?.label === "Project Stage" ||
                      item?.label === "Methodology" ||
                      item?.label === "Registry" ? (
                      <VeBox
                        sx={{
                          marginTop:
                            values["isMethodologyDeviation"] !== "yes" &&
                            item?.label === "Financing Type"
                              ? "0px"
                              : "0px",
                        }}
                      >
                        <CreatableSelect
                          name={item?.name}
                          label={item?.label}
                          // showSelectedLimit={item?.name === "methodology" ? 50 : 35}
                          control={control}
                          disabled={
                            (item?.label === "Sub Sector Group" &&
                              !values["sector"]) ||
                            (item?.label === "Sector" &&
                              !values["sectorGroup"]) ||
                            (item?.label === "Methodology" &&
                              !values["registry"]) ||
                            (data &&
                              (item?.label === "Sub Sector Group" ||
                                item?.label === "Sector" ||
                                item?.label === "Sector Group" ||
                                item?.label === "Project Stage"))
                          }
                          // value={item.value}
                          required={item?.required}
                          options={item?.options}
                          error={!!errors?.[`${item?.name!}`]}
                          errorText={errors?.[item?.name!]?.message}
                          isAdded={
                            item?.name === "ratedBy" ||
                            item?.label === "Market Label" ||
                            item?.name === "projectStatus"
                              ? true
                              : false
                          }
                          value={values ? values[`${item.name}`] : {}}
                          // error={!!errors?.[`${item.name}`]}
                          // errorText={errors?.[`${item.name}`]?.message || ""}
                        />
                      </VeBox>
                    ) : item?.label === "Financing Type" ? (
                      <VeBox
                        sx={{
                          marginTop:
                            values["isMethodologyDeviation"] !== "yes" &&
                            item?.label === "Financing Type"
                              ? "0px"
                              : "0px",
                        }}
                      >
                        <VeSelect
                          name={item?.name}
                          label={item?.label}
                          // showSelectedLimit={item?.name === "methodology" ? 50 : 35}
                          limiterLimit={150}
                          dropdownWidth={"200px"}
                          fontSize={"12px"}
                          multiple={item?.label === "Financing Type"}
                          control={control}
                          // value={item.value}
                          defaultValue={item.value}
                          required={item?.required}
                          options={item?.options}
                          error={!!errors?.[`${item?.name!}`]}
                          errorText={errors?.[item?.name!]?.message}
                        />
                      </VeBox>
                    ) : item?.label === "Deviation Details" &&
                      values["isMethodologyDeviation"] === "yes" ? (
                      <RichText
                        placeholder={item?.label}
                        value={item?.value || ""}
                        handleOnBlur={(text: string) =>
                          setValue("deviationDetails", text, {
                            shouldDirty: true,
                          })
                        }
                      />
                    ) : (
                      item?.label !== "Deviation Details" && (
                        <VeInput
                          label={item?.label}
                          required={item?.required}
                          register={register}
                          // value={item.value}
                          error={!!errors?.[`${item?.name!}`]}
                          errorText={errors?.[item?.name!]?.message}
                          name={item?.name}
                          type={
                            item?.name === "projectLength" ? "number" : "text"
                          }
                        />
                      )

                      // </VeBox>
                    )}
                  </VeBox>
                </Grid>
              ))}
          </Grid>
          <VeBox display={"flex"} flexDirection={"column"}>
            <VeTypography
              variant="label2StrongTertiary"
              sx={{ fontWeight: "500 !important" }}
            >
              Location
            </VeTypography>
            <VeTypography
              variant="label3Tertiary"
              marginTop={"8px"}
              marginBottom={"12px"}
            >
              Please provide the longitude and latitude of the sites where the
              project is located and being implemented. This information is
              essential for accurately mapping and tracking the project's
              geographical details.
            </VeTypography>
          </VeBox>
          <Grid container columnSpacing={"16px"} marginBottom={"20px"}>
            {coordinates?.map((item: any, mainIndex: number) => (
              <Grid container columnSpacing={"16px"} paddingLeft={"16px"}>
                <Grid
                  item
                  xl={5.75}
                  lg={5.75}
                  md={5.75}
                  sm={5.75}
                  xs={5.5}
                  mb={2}
                >
                  <VeInput
                    label={"Latitude"}
                    name={"latitude"}
                    value={item?.latitude}
                    error={
                      isSubmitted &&
                      ((coordinates[mainIndex]?.latitude?.length === 0 &&
                        coordinates[mainIndex]?.longitude?.length > 0) ||
                        (coordinates[mainIndex]?.latitude?.length > 0 &&
                          (Number(coordinates[mainIndex]?.latitude) < -90 ||
                            Number(coordinates[mainIndex]?.latitude) > 90)))
                    }
                    onKeyDown={(e: any) => {
                      // Allow only valid keys
                      if (
                        !/[0-9.\-]/.test(e.key) && // Allow numbers, ".", and "-"
                        ![
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }

                      // Prevent multiple "." in the input
                      if (e.key === "." && e.target.value.includes(".")) {
                        e.preventDefault();
                      }

                      // Prevent multiple "-" or "-" not at the start
                      if (
                        e.key === "-" &&
                        (e.target.value.includes("-") ||
                          e.target.selectionStart !== 0)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    errorText={"Latitude must be a number between -90 and 90."}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      // Restrict to numeric input with a maximum length of 8
                      if (value.length > (value?.includes(".") ? 9 : 8)) {
                        return;
                      }
                      setCoordinates(
                        coordinates?.map((item: any, index: number) => {
                          if (index === mainIndex) {
                            return {
                              ...item,
                              latitude: e.target.value,
                            };
                          } else return item;
                        })
                      );
                    }}
                    type={"text"}
                  />
                </Grid>
                <Grid
                  item
                  xl={5.75}
                  lg={5.75}
                  md={5.75}
                  sm={5.75}
                  xs={5.5}
                  mb={2}
                >
                  <VeInput
                    label={"Longitude"}
                    name={"longitude"}
                    value={item?.longitude}
                    error={
                      isSubmitted &&
                      ((coordinates[mainIndex]?.latitude?.length > 0 &&
                        coordinates[mainIndex]?.longitude?.length === 0) ||
                        (coordinates[mainIndex]?.longitude?.length > 0 &&
                          (Number(coordinates[mainIndex]?.longitude) < -180 ||
                            Number(coordinates[mainIndex]?.longitude) > 180)))
                      // (!coordinates[mainIndex]?.longitude || isNaN(Number(coordinates[mainIndex]?.longitude)) || Number(coordinates[mainIndex]?.longitude) < -180 || Number(coordinates[mainIndex]?.longitude) > 180)
                    }
                    onKeyDown={(e: any) => {
                      // Allow only valid keys
                      if (
                        !/[0-9.\-]/.test(e.key) && // Allow numbers, ".", and "-"
                        ![
                          "Backspace",
                          "Delete",
                          "ArrowLeft",
                          "ArrowRight",
                          "Tab",
                        ].includes(e.key)
                      ) {
                        e.preventDefault();
                      }

                      // Prevent multiple "." in the input
                      if (e.key === "." && e.target.value.includes(".")) {
                        e.preventDefault();
                      }

                      // Prevent multiple "-" or "-" not at the start
                      if (
                        e.key === "-" &&
                        (e.target.value.includes("-") ||
                          e.target.selectionStart !== 0)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    errorText={
                      "Longitude must be a number between -180 and 180."
                    }
                    onChange={(e: any) => {
                      const value = e.target.value;
                      // Restrict to numeric input with a maximum length of 8
                      if (value.length > (value?.includes(".") ? 9 : 8)) {
                        return;
                      }

                      setCoordinates(
                        coordinates?.map((item: any, index: any) => {
                          if (index === mainIndex) {
                            return {
                              ...item,
                              longitude: e.target.value,
                            };
                          } else return item;
                        })
                      );
                    }}
                    type={"text"}
                  />
                </Grid>
                <Grid
                  onClick={() => {
                    coordinates?.length > 1 &&
                      setCoordinates(
                        coordinates.filter(
                          (item: any, newIndex: number) =>
                            newIndex !== mainIndex
                        )
                      );
                  }}
                  item
                  xl={0.5}
                  lg={0.5}
                  md={0.5}
                  sm={0.5}
                  xs={1}
                  mb={2}
                  display={"flex"}
                  justifyContent={"flex-end"}
                  alignItems={"center"}
                  paddingLeft={"0px !important"}
                >
                  <RemoveIcon defaultFill="#5F6368" hoverFill="#FF0000" />
                </Grid>
              </Grid>
            ))}
            <VeBox
              paddingLeft={"16px"}
              marginTop={"-10px"}
              marginBottom={"16px"}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setCoordinates([
                  ...coordinates,
                  ...[
                    {
                      latitude: "",
                      longitude: "",
                    },
                  ],
                ]);
              }}
            >
              <VeTypography
                variant="label3StrongBrand"
                marginTop={"8px"}
                marginBottom={"12px"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                {DynamicImage(assets?.add, "close", 10, 10)}{" "}
                <VeTypography
                  variant="label3StrongBrand"
                  marginLeft={"5px"}
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  Add
                </VeTypography>
              </VeTypography>
            </VeBox>
            <VeBox width={"100%"} paddingLeft={"16px"} mb={2}>
              <VeTypography
                variant="label2StrongTertiary"
                sx={{ fontWeight: "500 !important" }}
              >
                KML File
              </VeTypography>
              <VeBox marginTop={"6px"}>
                <input
                  type="file"
                  id={"upload"}
                  name={"upload"}
                  hidden
                  accept=".kml,.kmz,.shp,.shx,.dbf,.geojson,.gpkg,.gpx,.tiff,.tif,.csv,.gml,.dxf"
                  onChange={handleFileChange}
                  // accept={allowedTypes?.join(",")}
                />
                <label htmlFor={"upload"}>
                  <Grid
                    container
                    sx={{
                      height: "40px",
                      cursor: "pointer",
                      border: "1px solid #E8E8E8",
                      borderRadius: "4px",
                    }}
                  >
                    <Grid
                      item
                      xl={10.5}
                      lg={10}
                      md={10.5}
                      sm={10.5}
                      xs={8}
                      mb={2}
                      display={"flex"}
                      height={"100%"}
                      alignItems={"center"}
                    >
                      <VeTypography
                        variant="label2Tertiary"
                        paddingLeft={"16px"}
                      >
                        Click here to upload
                      </VeTypography>
                    </Grid>
                    <Grid item xl={1.5} lg={2} md={1.5} sm={1.5} xs={4} mb={2}>
                      <VeBox
                        // paddingRight={"16px"}
                        display={"flex"}
                        height={"100%"}
                        alignItems={"center"}
                        borderLeft={"1px solid #E8E8E8"}
                        justifyContent={"center"}
                        paddingRight={"0px !important"}
                      >
                        <VeTypography
                          variant="label3StrongBrand"
                          display={"flex"}
                          justifyContent={"center"}
                          alignItems={"center"}
                        >
                          {DynamicImage(assets?.upload, "close", 16, 16)}{" "}
                          <VeTypography
                            className="document_title"
                            variant="label3StrongBrand"
                            marginLeft={"5px"}
                            display={"flex"}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            Upload
                          </VeTypography>
                        </VeTypography>
                      </VeBox>
                    </Grid>
                  </Grid>
                </label>
                {fileUploading && (
                  <VeBox
                    display="flex"
                    flexDirection="row"
                    gap="8px"
                    marginTop="6px"
                  >
                    <CircularProgress sx={{ color: "var(--vc-violet)" }} size={10} />
                    <VeBox display="flex" alignItems="center">
                      <VeTypography variant="label4MainBrand">
                        Uploading
                      </VeTypography>
                    </VeBox>
                  </VeBox>
                )}
                {/* {files?.length > 0 && <Grid container marginTop={"16px"}>
                                    {files?.map((item: any, mainIndex: number) => (
                                        <DocumentCard onRemove={() => {
                                            setFiles(files.filter((item: any, newIndex: number) => newIndex !== mainIndex))
                                        }}
                                            DynamicImage={DynamicImage}
                                            assets={assets}
                                            item={item}
                                            formatFileSize={formatFileSize}
                                        />
                                    ))}
                                </Grid>} */}
                {files?.length > 0 && (
                  <VeBox mt={1} width={"300px"}>
                    <UploadDocumentCard
                      item={files?.[0]}
                      image={DynamicImage}
                      fileTypeImages={variables.fileTypeImages}
                      setFileSelected={setFiles}
                      setFileKey={setFileKey}
                      edit={true}
                      // admin={true}
                      multiple={true}
                      fileSizeRequired={false}
                    />
                  </VeBox>
                )}
              </VeBox>
            </VeBox>
            {fields
              ?.filter((item: any) => item.section === "location")
              ?.map((item: any) => (
                <Grid item xl={6} lg={6} md={6} sm={6} xs={12} mb={2}>
                  <VeBox>
                    {item?.label === "Co-Certifications" ? (
                      <VeBox>
                        <VeSelect
                          name={item?.name}
                          label={item?.label}
                          // showSelectedLimit={item?.name === "methodology" ? 50 : 35}
                          limiterLimit={150}
                          dropdownWidth={"200px"}
                          fontSize={"12px"}
                          control={control}
                          // value={item.value}
                          defaultValue={item.value}
                          required={item?.required}
                          options={item?.options}
                          error={!!errors?.[`${item?.name!}`]}
                          errorText={errors?.[item?.name!]?.message}
                        />
                      </VeBox>
                    ) : (
                      item?.name !== "internalNote" &&
                      item?.label !== "Deviation Details" && (
                        <VeInput
                          label={item?.label}
                          required={item?.required}
                          register={register}
                          // value={item.value}
                          error={!!errors?.[`${item?.name!}`]}
                          errorText={errors?.[item?.name!]?.message}
                          name={item?.name}
                          type={"text"}
                        />
                      )
                    )}
                  </VeBox>
                </Grid>
              ))}
            {fields
              ?.filter((item: any) => item.section === "proponent")
              ?.map(
                (item: any) =>
                  ((values["isMainProponent"] === "no" &&
                    item?.name !== "isMainProponent") ||
                    item?.name === "isMainProponent") && (
                    <Grid
                      item
                      xl={
                        item?.name === "companyName" ||
                        item?.name === "address1" ||
                        item?.name === "address2" ||
                        item?.name === "isMainProponent"
                          ? 12
                          : 6
                      }
                      lg={
                        item?.name === "companyName" ||
                        item?.name === "address1" ||
                        item?.name === "address2" ||
                        item?.name === "isMainProponent"
                          ? 12
                          : 6
                      }
                      md={
                        item?.name === "companyName" ||
                        item?.name === "address1" ||
                        item?.name === "address2" ||
                        item?.name === "isMainProponent"
                          ? 12
                          : 6
                      }
                      sm={
                        item?.name === "isMainProponent" ||
                        item?.name === "companyName"
                          ? 12
                          : 6
                      }
                      xs={12}
                      mb={2}
                    >
                      <VeBox>
                        {item?.name === "registrationCountry" &&
                        values["isMainProponent"] === "no" ? (
                          <VeCountry
                            value={values[item?.name]}
                            // style={{ maxWidth: "400px" }}
                            register={register}
                            setValue={setValue}
                            name={item?.name}
                            label={item?.label}
                            required={item?.required}
                            error={!!errors?.[`${item?.name!}`]}
                            errorText={errors?.[item?.name!]?.message}
                          />
                        ) : item?.name === "isMainProponent" ? (
                          <VeBox>
                            <VeTypography
                              variant="label2StrongTertiary"
                              marginTop={"0px !important"}
                              display={"flex"}
                              sx={{ fontWeight: "500 !important" }}
                            >
                              {item?.label}
                              <VeBox marginLeft={"5px"} color={"red"}>
                                *
                              </VeBox>
                            </VeTypography>
                            <VeBox sx={{ marginTop: "5px", marginLeft: "5px" }}>
                              <VeRadioGroup
                                name={item?.name}
                                options={item?.options}
                                control={control}
                              />
                            </VeBox>
                            {values["isMainProponent"] === "no" && (
                              <VeBox sx={{ marginTop: "8px !important" }}>
                                <VeTypography variant="label2StrongTertiary">
                                  Project Proponent Details
                                </VeTypography>
                              </VeBox>
                            )}
                            {!values["isMainProponent"] && isSubmitted && (
                              <VeBox sx={{ marginTop: "-5px !important" }}>
                                <VeTypography variant="label4ErrorPrimary">
                                  {errors?.["isMainProponent"]?.message}
                                </VeTypography>
                              </VeBox>
                            )}
                          </VeBox>
                        ) : (
                          item?.name !== "internalNote" &&
                          values["isMainProponent"] === "no" && (
                            <VeInput
                              label={item?.label}
                              required={item?.required}
                              register={register}
                              // value={values[item.name]}
                              error={!!errors?.[`${item?.name!}`]}
                              errorText={errors?.[item?.name!]?.message}
                              name={item?.name}
                              type={
                                item?.name === "zipcode" ? "number" : "text"
                              }
                            />
                          )

                          // </VeBox>
                        )}
                      </VeBox>
                    </Grid>
                  )
              )}
          </Grid>
          <VeBox ref={bottomRef} /> {/* Empty div to scroll to */}
        </VeBox>
        {/* </PageWrapper> */}
      </form>
    </>
  );
};

export default ProjectStep1;
